/**
 * Pantalla "Mi historial" del vecino (Sprint 18 P7).
 *
 * Línea de tiempo de eventos del lote (inscripciones, validaciones,
 * recursos, pagos a cuotas, cambios de tenencia). Derivada on-read en el
 * backend. El vecino ve solo desde el inicio de su propia tenencia
 * (recorte de privacidad, P23/P25). Distinto del historial financiero de
 * "Mi cuenta".
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { ApiError } from '../../lib/api/client';
import { casasDelVecino } from '../../lib/casas-vecino';
import { getHistorialLote, type EventoHistorial } from '../../lib/api/lotes';
import { HistorialTimeline } from '../../components/HistorialTimeline';
import { InfoNote } from '../../components/ui/InfoNote';

export function MiHistorial() {
  const { me } = useAuth();
  // Casas del vecino: tenencias (titular) + casas donde es autorizado.
  const casas = casasDelVecino(me);
  const [loteSeleccionado, setLoteSeleccionado] = useState<string>(
    casas[0]?.lote_id ?? '',
  );
  const [eventos, setEventos] = useState<EventoHistorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loteSeleccionado) {
      setLoading(false);
      return;
    }
    let cancelado = false;
    setLoading(true);
    setError(null);
    getHistorialLote(loteSeleccionado)
      .then((data) => {
        if (!cancelado) setEventos(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar el historial`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [loteSeleccionado]);

  if (!me) return null;

  return (
    <div>
      <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 pt-6 pb-4">
        <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
          Mi historial
        </div>
        <h1 className="text-2xl font-medium text-text-primary leading-tight">
          Historial de mi casa
        </h1>
        {casas.length > 1 && (
          <label className="text-sm text-text-secondary mt-2 inline-block">
            Casa:{' '}
            <select
              value={loteSeleccionado}
              onChange={(e) => setLoteSeleccionado(e.target.value)}
              className="rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary text-sm ml-2 px-2 py-1"
            >
              {casas.map((t) => (
                <option key={t.lote_id} value={t.lote_id}>
                  {t.lote_identificador}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-4">
        {loading && (
          <p className="text-sm text-text-muted">Cargando historial…</p>
        )}
        {error && !loading && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        {!loading && !error && (
          <>
            <HistorialTimeline eventos={eventos} />
            <InfoNote>
              Mostramos los eventos de tu casa desde que comenzó tu tenencia.
              El historial financiero detallado está en "Mi cuenta".
            </InfoNote>
          </>
        )}
      </div>
    </div>
  );
}
